import DiseaseEncyclopediaModel from '@/entities/disease-encyclopedia/model/diseaseEncyclopedia';
import { createDiseaseEncyclopediaDetailUrlWithKeyword } from '@/entities/disease-encyclopedia/util';
import useCustomRouter from '@/hooks/useCustomRouter';
import useNavigator from '@/hooks/useNavigator';
import { ROUTES } from '@/types/common';
import { EIntegratedSearchTabType, ERequestButtonType } from '@/types/search';
import { coreLogger } from '@/utils/logger';
import useGetKeywordSearchDiseaseEncyclopediaInfinite from './useGetKeywordSearchDiseaseEncyclopediaInfinite';

interface Props {
  keyword: string;
  tab: EIntegratedSearchTabType;
}

export interface SearchDiseaseEncyclopediaViewModel {
  keyword: string;
  diseaseEncyclopedia: Array<DiseaseEncyclopediaModel>;
  isDiseaseEncyclopediaLoading: boolean;
  hasDiseaseEncyclopediaNextPage: boolean | undefined;
  fetchDiseaseEncyclopediaNextPage: () => void;
  onClickDiseaseEncyclopedia: (diseaseEncyclopediaId: string) => () => void;
  onClickLogDiseaseEncyclopedia: (diseaseEncyclopediaId: string) => void;
  handleClickRequestButton: () => void;
}

const useSearchDiseaseEncyclopediaViewModel = ({ keyword, tab }: Props): SearchDiseaseEncyclopediaViewModel => {
  const { pageName, moveToPage } = useCustomRouter();
  const { pushScreen } = useNavigator();
  const {
    diseaseEncyclopediaData,
    isDiseaseEncyclopediaLoading,
    hasDiseaseEncyclopediaNextPage,
    fetchDiseaseEncyclopediaNextPage,
  } = useGetKeywordSearchDiseaseEncyclopediaInfinite({
    keyword,
    enabled: tab === EIntegratedSearchTabType.DiseaseEncyclopedia,
  });

  const handleClickRequestButton = () => {
    coreLogger({
      eventName: 'request_additional_info_click',
      pageName: pageName,
      payload: {
        position: ERequestButtonType.BOTTOM,
        keyword,
        section_name: 'integrate_search_hospital',
      },
    });
    moveToPage({
      webLink: `${ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.path}`,
      mobileScreenName: ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.appScreenName,
      params: { keyword },
    });
  };

  const onClickLogDiseaseEncyclopedia = (diseaseEncyclopediaId: string) => {
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: diseaseEncyclopediaId,
        tab: EIntegratedSearchTabType.DiseaseEncyclopedia,
      },
    });
  };

  const onClickDiseaseEncyclopedia = (diseaseEncyclopediaId: string) => () => {
    pushScreen({
      url: createDiseaseEncyclopediaDetailUrlWithKeyword(diseaseEncyclopediaId, keyword),
    });
  };

  return {
    keyword,
    diseaseEncyclopedia: diseaseEncyclopediaData?.pages.flatMap((diseaseEncyclopedia) => diseaseEncyclopedia) ?? [],
    isDiseaseEncyclopediaLoading,
    hasDiseaseEncyclopediaNextPage,
    fetchDiseaseEncyclopediaNextPage,
    onClickDiseaseEncyclopedia,
    onClickLogDiseaseEncyclopedia,
    handleClickRequestButton,
  };
};

export default useSearchDiseaseEncyclopediaViewModel;
